import React, { useState, useRef, useEffect } from "react"
import { graphql } from "gatsby"
import axios from "axios"
import { Formik, Form, Field, ErrorMessage, withFormik } from "formik"
import Modal from "react-modal"
import { useMediaQuery } from "react-responsive"
import * as yup from "yup"
import Layout from "../components/layout"
import SEO from "../components/seo"
import Img from "gatsby-image"
import validations from "../utils/validations"
import serverValidations from "../utils/serverValidations"
import { CustomerServiceApi, SalesApi } from "../api/contact-api"
import camelcase from "lodash.camelcase"
import styles from "../styles/contact.module.scss"
import formStyles from "../styles/form-styles.module.scss"
import chevronDown from "../images/chevron-down.svg"
import closeIcon from "../images/close-icon.svg"

export const query = graphql`
  query CONTACT {
    wpgraphql {
      corePageBy(corePageId: 7138) {
        id
        seo {
          metaDesc
          title
          opengraphTitle
          opengraphDescription
          twitterDescription
          twitterTitle
        }
        acfContact {
          title
          subText
          salesReps {
            rep {
              sourceUrl
              imageFile {
                childImageSharp {
                  fluid(quality: 90, maxWidth: 50) {
                    ...GatsbyImageSharpFluid_withWebp_noBase64
                  }
                }
              }
            }
          }
          contactBoxes {
            label
            icon {
              altText
              sourceUrl
              slug
            }
            title
            subtext
            buttonText
          }
        }
      }
    }
  }
`

let customerServiceValidation = yup.object().shape({
  name: yup
    .string()
    .required("Enter a name")
    .max(25, "Name name must not exceed 40 characters"),
  email: yup
    .string()
    .min(8, "Email must have 8 characters")
    .max(255, "Email must not exceed 255 characters")
    .required("Enter an email address")
    .email("Must be a valid email address"),
  phone: yup
    .string()
    .trim()
    .max(15, "Must not exceed 15 digits")
    .required("Enter a phone number")
    .test("CC Phone", "Invalid phone number", function(value) {
      if (value === undefined || value === null) return true
      return validations.validatePhoneNumber(value)
    }),
  description: yup
    .string()
    .max(4000, "Must not exceed 4000 characters")
    .required("Enter a message"),
})

const CustomerServiceForm = () => {
  const caseFormEl = useRef(null)
  const ipEl = useRef(null)

  const submitServicePost = (
    values,
    { setSubmitting, resetForm, setErrors }
  ) => {
    setSubmitting(true)

    const data = {
      Name: values.name,
      Phone: values.phone,
      EmailAddress: values.email,
      Comments: values.description,
    }

    CustomerServiceApi(
      data,
      ipEl,
      caseFormEl,
      setErrors,
      serverValidations.createServerServiceSchema,
      setSubmitting
    )
  }

  const Form = withFormik({
    handleSubmit: submitServicePost,
    validationSchema: customerServiceValidation,
    mapPropsToValues: props => ({
      name: "",
      email: "",
      phone: "",
      description: "",
    }),
  })(props => {
    const {
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      touched,
      errors,
      values,
    } = props

    return (
      <form
        action={process.env.GATSBY_SALESFORCE_CASE_URL}
        method="POST"
        onSubmit={handleSubmit}
        ref={caseFormEl}
      >
        <div className={styles.formTitle}>Contact Customer Service</div>
        <input
          type="hidden"
          name="orgid"
          value={process.env.GATSBY_SALESFORCE_OID}
        />
        <input
          type="hidden"
          name="retURL"
          value={`${process.env.GATSBY_SITE_URL}/confirm-support`}
        />

        {/* Ip Address */}
        <input
          type="hidden"
          id="00Nf400000A3ZPi"
          name="00Nf400000A3ZPi"
          maxLength="255"
          value=""
          ref={ipEl}
        />

        {/* Url Source */}
        <input
          type="hidden"
          id="00Nf4000009OQ28"
          name="00Nf4000009OQ28"
          value={`${process.env.GATSBY_SITE_URL}/contact`}
        />

        {/* Brand */}
        <input
          type="hidden"
          id="00Nf4000009OaAV"
          name="00Nf4000009OaAV"
          value="ConferenceCalling.com"
        />

        {/* Type */}
        <input type="hidden" id="type" name="type" value="Service" />

        {/* Origin */}
        <input type="hidden" id="origin" name="origin" value="Web" />

        {/* Subject */}
        <input
          type="hidden"
          id="subject"
          name="subject"
          maxLength="80"
          size="20"
          value="Contact Us"
        />

        <div className={styles.nameContainer}>
          <label className={formStyles.label} htmlFor="name">
            <div className={formStyles.required}>Name</div>
          </label>
          <input
            className={`${
              touched.name && errors.name ? formStyles.error : null
            } ${formStyles.input}`}
            id="name"
            name="name"
            type="text"
            value={values.name}
            onBlur={handleBlur}
            onChange={handleChange}
            maxLength="80"
            placeholder="Your Name"
          />
          <ErrorMessage name="name">
            {msg => <div className={formStyles.errorTip}>{msg}</div>}
          </ErrorMessage>
        </div>

        <div className={styles.phoneEmailRow}>
          <div className={styles.emailContainer}>
            <label className={formStyles.label} htmlFor="email">
              <div className={formStyles.required}>Email Address</div>
            </label>
            <input
              className={`${
                touched.email && errors.email ? formStyles.error : null
              } ${formStyles.input}`}
              id="email"
              name="email"
              type="text"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Your Email Address"
            />
            <ErrorMessage name="email">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>

          <div className={styles.phoneContainer}>
            <label className={formStyles.label} htmlFor="phone">
              <div className={formStyles.required}>Phone Number</div>
            </label>
            <input
              className={`${
                touched.phone && errors.phone ? formStyles.error : null
              } ${formStyles.input}`}
              id="phone"
              name="phone"
              type="text"
              value={values.phone}
              onBlur={handleBlur}
              onChange={handleChange}
              maxLength="20"
              placeholder="Phone Number"
            />
            <ErrorMessage name="phone">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>
        </div>

        <div className={styles.messageContainer}>
          <label className={formStyles.label} htmlFor="description">
            <div className={formStyles.required}>Message</div>
          </label>
          <textarea
            className={`${
              touched.description && errors.description
                ? formStyles.error
                : null
            } ${formStyles.textArea}`}
            id="medescriptionssage"
            name="description"
            type="text"
            value={values.description}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="Your Message"
          />
          <ErrorMessage name="description">
            {msg => <div className={formStyles.errorTip}>{msg}</div>}
          </ErrorMessage>
        </div>

        <div className={styles.submitContainer}>
          <button
            disabled={isSubmitting}
            type="submit"
            style={{ opacity: isSubmitting ? ".5" : "1" }}
          >
            Send Message
          </button>
        </div>
      </form>
    )
  })

  return <Form />
}

let salesValidation = yup.object().shape({
  first_name: yup
    .string()
    .required("Enter a first name")
    .max(25, "First name must not exceed 25 characters"),
  last_name: yup
    .string()
    .required("Enter a last name")
    .max(25, "Last name must not exceed 25 characters"),
  email: yup
    .string()
    .min(8, "Email must have 8 characters")
    .max(255, "Email must not exceed 255 characters")
    .required("Enter an email address")
    .email("Must be a valid email address"),
  phone: yup
    .string()
    .trim()
    .max(15, "Must not exceed 15 digits")
    .required("Enter a phone number")
    .test("CC Phone", "Invalid phone number", function(value) {
      if (value === undefined || value === null) return true
      return validations.validatePhoneNumber(value)
    }),
  company: yup
    .string()
    .required("Enter a company name")
    .max(50, "Company name must not exceed 50 characters"),
  "00Nf4000009OQ2D": yup
    .string()
    .required("Select company size")
    .max(50, "Company name must not exceed 50 characters"),
  description: yup
    .string()
    .max(4000, "Must not exceed 4000 characters")
    .required("Enter a message"),
})

const SelectCompanySize = ({
  values,
  handleChange,
  handleBlur,
  touched,
  errors,
  isMobile,
}) => (
  <select
    className={`${
      touched["00Nf4000009OQ2D"] && errors["00Nf4000009OQ2D"]
        ? formStyles.error
        : null
    } ${formStyles.select}`}
    name="00Nf4000009OQ2D"
    value={values["00Nf4000009OQ2D"]}
    onChange={handleChange}
    onBlur={handleBlur}
    style={{
      background: `url('${chevronDown}') no-repeat`,
      backgroundPosition: "right 15px center",
      backgroundSize: "auto",
    }}
  >
    <option value="" defaultValue isdisabled="true">
      Size
    </option>
    <option value="1-5">1-5</option>
    <option value="6-10">6-10</option>
    <option value="11-50">11-50</option>
    <option value="51-99">51-99</option>
    <option value="100-499">100-499</option>
    <option value="500-1,000">500-1,000</option>
    <option value="1,001-10,000">1,001-10,000</option>
  </select>
)

const SalesForm = () => {
  const salesFormEl = useRef(null)
  const ipEl = useRef(null)

  const submitSalesPost = (values, { setSubmitting, resetForm, setErrors }) => {
    // alert(JSON.stringify(values, null, 2))
    setSubmitting(true)

    const data = {
      FirstName: values.first_name,
      LastName: values.last_name,
      Company: values.company,
      CompanySize: values["00Nf4000009OQ2D"],
      Phone: values.phone,
      EmailAddress: values.email,
      Comments: values.description,
      ContactTo: "sales",
    }

    SalesApi(
      data,
      ipEl,
      salesFormEl,
      setErrors,
      serverValidations.createServerLeadSchema,
      setSubmitting
    )
  }

  const Form = withFormik({
    handleSubmit: submitSalesPost,
    validationSchema: salesValidation,
    mapPropsToValues: props => ({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      "00Nf4000009OQ2D": "", // company size
      description: "",
    }),
  })(props => {
    const {
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      touched,
      errors,
      values,
    } = props

    return (
      <form
        action={process.env.GATSBY_SALESFORCE_LEAD_URL}
        method="POST"
        onSubmit={handleSubmit}
        ref={salesFormEl}
      >
        <div className={styles.formTitle}>Contact Sales Team</div>
        <input
          type="hidden"
          name="oid"
          value={process.env.GATSBY_SALESFORCE_OID}
        />
        <input
          type="hidden"
          name="retURL"
          value={`${process.env.GATSBY_SITE_URL}/confirm-sales?src=contactsales`}
        />

        {/* Lead Source */}
        <input
          type="hidden"
          id="lead_source"
          name="lead_source"
          value="Contact Us Form"
        />

        {/* Ip Address */}
        <input
          type="hidden"
          id="00Nf4000009OQ28"
          name="00Nf4000009OQ28"
          maxLength="255"
          value=""
          ref={ipEl}
        />

        {/* Brand */}
        <input
          type="hidden"
          id="Brand__c"
          name="Brand__c"
          value="conferencecalling.com"
        />

        {/* Campaign */}
        <input
          type="hidden"
          id="00Nf4000009oqk6"
          name="00Nf4000009oqk6"
          maxLength="80"
          size="20"
          value={typeof window !== "undefined" && window.sbjs.get.first.cmp}
        />

        {/* Content */}
        <input
          type="hidden"
          id="00Nf4000009oqkB"
          name="00Nf4000009oqkB"
          maxLength="80"
          size="20"
          value={typeof window !== "undefined" && window.sbjs.get.first.cnt}
        />

        {/* Medium */}
        <input
          type="hidden"
          id="00Nf4000009oqkG"
          name="00Nf4000009oqkG"
          maxLength="80"
          size="20"
          value={typeof window !== "undefined" && window.sbjs.get.first.mdm}
        />

        {/* Source */}
        <input
          type="hidden"
          id="00Nf4000009oqkL"
          name="00Nf4000009oqkL"
          maxLength="80"
          size="20"
          value={typeof window !== "undefined" && window.sbjs.get.first.src}
        />

        {/* Term */}
        <input
          type="hidden"
          id="00Nf4000009oqkQ"
          name="00Nf4000009oqkQ"
          maxLength="80"
          size="20"
          value={typeof window !== "undefined" && window.sbjs.get.first.trm}
        />

        {/* <input type="hidden" name="debug" value="1" /> */}

        <div className={styles.nameRow}>
          <div className={styles.firstNameContainer}>
            <label className={formStyles.label} htmlFor="first_name">
              <div className={formStyles.required}>First Name</div>
            </label>
            <input
              className={`${
                touched.first_name && errors.first_name
                  ? formStyles.error
                  : null
              } ${formStyles.input}`}
              id="first_name"
              name="first_name"
              type="text"
              value={values.first_name}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="First Name"
            />
            <ErrorMessage name="first_name">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>

          <div className={styles.lastNameContainer}>
            <label className={formStyles.label} htmlFor="last_name">
              <div className={formStyles.required}>Last Name</div>
            </label>
            <input
              className={`${
                touched.last_name && errors.last_name ? formStyles.error : null
              } ${formStyles.input}`}
              id="last_name"
              name="last_name"
              type="text"
              value={values.last_name}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Last Name"
            />
            <ErrorMessage name="last_name">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>
        </div>

        <div className={styles.phoneEmailRow}>
          <div className={styles.emailContainer}>
            <label className={formStyles.label} htmlFor="email">
              <div className={formStyles.required}>Work Email</div>
            </label>
            <input
              className={`${
                touched.email && errors.email ? formStyles.error : null
              } ${formStyles.input}`}
              id="email"
              name="email"
              type="text"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Work Email"
            />
            <ErrorMessage name="email">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>

          <div className={styles.phoneContainer}>
            <label className={formStyles.label} htmlFor="phone">
              <div className={formStyles.required}>Work Phone</div>
            </label>
            <input
              className={`${
                touched.phone && errors.phone ? formStyles.error : null
              } ${formStyles.input}`}
              id="phone"
              name="phone"
              type="text"
              value={values.phone}
              onBlur={handleBlur}
              onChange={handleChange}
              maxLength="20"
              placeholder="Work Phone"
            />
            <ErrorMessage name="phone">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>
        </div>

        <div className={styles.companySizeRow}>
          <div className={styles.companyContainer}>
            <label className={formStyles.label} htmlFor="company">
              <div className={formStyles.required}>Company</div>
            </label>
            <input
              className={`${
                touched.company && errors.company ? formStyles.error : null
              } ${formStyles.input}`}
              id="company"
              name="company"
              type="text"
              value={values.company}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Company"
            />
            <ErrorMessage name="company">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>

          <div className={styles.sizeContainer}>
            <label className={formStyles.label} htmlFor="00Nf4000009OQ2D">
              <div className={formStyles.required}>Company Size</div>
            </label>
            <SelectCompanySize
              values={values}
              handleChange={handleChange}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              // isMobile={isMobile}
            />
            <ErrorMessage name="00Nf4000009OQ2D">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>
        </div>

        <div className={styles.messageContainer}>
          <label className={formStyles.label} htmlFor="description">
            <div className={formStyles.required}>Message</div>
          </label>
          <textarea
            className={`${
              touched.description && errors.description
                ? formStyles.error
                : null
            } ${formStyles.textArea}`}
            id="description"
            name="description"
            value={values.description}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder="Your Message"
          />
          <ErrorMessage name="description">
            {msg => <div className={formStyles.errorTip}>{msg}</div>}
          </ErrorMessage>
        </div>
        <div className={styles.submitContainer}>
          <button type="submit">Send Message</button>
        </div>
      </form>
    )
  })

  return <Form />
}

const Contact = ({ data }) => {
  const [messageFor, setMessageFor] = useState("cs")
  const [isModalOpen, setModalOpen] = useState(false)

  const handleSetModalOpen = () => setModalOpen(!isModalOpen)

  const handleSetMessageCustomerService = () => {
    setMessageFor("cs")
    handleSetModalOpen()
  }
  const handleSetMessageSalesTeam = () => {
    setMessageFor("sales")
    handleSetModalOpen()
  }

  const seo = data.wpgraphql.corePageBy.seo
  const contactData = data.wpgraphql.corePageBy.acfContact
  const isMobile = useMediaQuery({ maxWidth: 650 })

  const customModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      width: "550px",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      boxShadow: "0px 7px 80px rgba(0, 0, 0, 0.1)",
      border: "none",
      padding: "40px 30px",
      paddingTop: "50px",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, .6)",
      zIndex: 9999,
    },
  }

  const customModalStylesMobile = {
    content: {
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      width: "100%",
      borderRadius: "0",
      padding: "40px 30px",
      paddingTop: "50px",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, .6)",
      zIndex: 9999,
    },
  }

  return (
    <Layout isIntercomOnload>
      <SEO {...seo} />
      <Modal
        isOpen={isModalOpen}
        style={isMobile ? customModalStylesMobile : customModalStyles}
        onRequestClose={handleSetModalOpen}
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
      >
        <div className={styles.formContainer}>
          <button
            onClick={handleSetModalOpen}
            className={styles.modalCloseButton}
          >
            <img src={closeIcon} alt="close icon" />
          </button>
          {messageFor === "cs" ? <CustomerServiceForm /> : <SalesForm />}
        </div>
      </Modal>
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <h1>{contactData.title}</h1>
          <div dangerouslySetInnerHTML={{ __html: contactData.subText }} />
          <div className={formStyles.supportRepsContainer}>
            {contactData.salesReps.map((rep, index) => (
              <div key={index} className={formStyles.rep}>
                <Img
                  fluid={rep.rep.imageFile.childImageSharp.fluid}
                  alt="rep"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.bgWrapper}>
        <div className={styles.contactsContainer}>
          {contactData.contactBoxes.map(box => (
            <div key={box.label} className={styles.contactBox}>
              <div className={styles.iconContainer}>
                <img
                  src={`/images/acf-icons/${box.icon.slug}.svg`}
                  alt={box.icon.altText}
                />
              </div>
              <div className={styles.main}>
                <div className={styles.text}>
                  <h3>{box.title}</h3>
                  <div dangerouslySetInnerHTML={{ __html: box.subtext }} />
                </div>
                {box.label === "Customer Service" && (
                  <a
                    onClick={handleSetMessageCustomerService}
                    className={styles.modalButton}
                    href="#"
                  >
                    {box.buttonText}
                  </a>
                )}
                {box.label === "Send Message" && (
                  <>
                    <div
                      className={styles.numberContainer}
                      style={{ transform: "translateY(-4px)" }}
                    >
                      <a href="tel:+18884989240" className={styles.number}>
                        {/* prettier-ignore */}
                        <svg height="14" width="14" xmlns="http://www.w3.org/2000/svg" ><path d="M2.816 6.059a11.782 11.782 0 005.125 5.125l1.711-1.71c.21-.21.521-.28.794-.187a8.872 8.872 0 002.776.443.78.78 0 01.778.778v2.714a.78.78 0 01-.778.778C5.92 14 0 8.081 0 .778A.78.78 0 01.778 0H3.5a.78.78 0 01.778.778c0 .972.155 1.905.443 2.776a.78.78 0 01-.194.794z" fill="#6c57c2" /></svg>
                        <span>1.888.498.9240</span>
                      </a>
                    </div>
                    <a
                      id="intercom_link"
                      className={styles.modalButton}
                      href="#"
                      style={{ transform: "translateY(-4px)" }}
                    >
                      {box.buttonText}
                    </a>
                  </>
                )}
                {box.label === "Sales Team" && (
                  <a
                    onClick={handleSetMessageSalesTeam}
                    className={styles.modalButton}
                    href="#"
                  >
                    {box.buttonText}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  )
}

export default Contact
export { SalesForm, CustomerServiceForm }
